/**
 * @file Album Manual Matching toolbar actions component.
 */

import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';
import { useMediaQuery } from 'react-responsive';

import Button from '../../../../../../../../../components/global/Button';
import { PHOTO_ALBUM_FOLDER_TAGS } from '../../../../../../../../../constants/modals';
import TrashIcon from '../../../../../../../../../components/icons/TrashIcon';
import { breakpointDown } from '../../../../../../../../../services/responsive/responsive';
import { PRO_MULTI_TARGET_SELECTION_MODAL } from '../../../../../../../components/MultiTargetSelection/components/ProMultiTargetSelection/modal';
import Angle from '../../../../../../../../../components/global/Angle';
import { canAccessOriginalDownloadsOnBasename } from '../../../../../../../../../util/product-permissions';
import DropdownMenu from '../../../../../../../../../components/global/DropdownMenu';
import useBasename from '../../../../../../../../../components/routing/useBasename';
import useContributorPermission from '../../../../../../../services/contributors/hooks/useContributorPermission';

const propTypes = {
  dismissPhotoGroupToolbar: PropTypes.func.isRequired,
  selectedPhotos: PropTypes.arrayOf(PropTypes.string),
  openModal: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onPhotosDelete: PropTypes.func,
  onPhotosDownload: PropTypes.func,
  onPhotosHide: PropTypes.func,
  onFaceTagModeToggle: PropTypes.func,
  onRemoveFaceTags: PropTypes.func,
  onAddFauxFaceTags: PropTypes.func,
  availableIdentities: PropTypes.array,
  album: PropTypes.shape({
    id: PropTypes.string,
    root_album_folder: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

const AlbumManualMatchingToolbar = ({
  dismissPhotoGroupToolbar,
  selectedPhotos,
  openModal,
  onPhotosDelete,
  disabled,
  onPhotosDownload,
  onPhotosHide,
  onFaceTagModeToggle,
  onRemoveFaceTags,
  onAddFauxFaceTags,
  availableIdentities,
  album,
}) => {
  const basename = useBasename();
  const { isContributor } = useContributorPermission({
    entityId: album?.id,
  });
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const isReferencePhotosAlbum = !!album?.is_reference_photos_album;
  const [openNav, setOpenNav] = useState(false);
  const totalSelectedPhotos = selectedPhotos.length;

  const photoLabel = pluralize('Photo', totalSelectedPhotos);

  const classes = classNames({
    'action-toolbar action-toolbar-bottom-spacer': true,
    'photo-group-toolbar': true,
    'action-toolbar-visible': (selectedPhotos?.length ?? 0) !== 0,
  });
  const buttonClasses = classNames({
    'text-extra-bold': true,
    'text-primary': !isMobile,
    'text-white': isMobile,
  });
  const deleteButtonClasses = classNames({
    'text-danger': true,
    'text-extra-bold': isMobile,
  });
  const buttonContainerClasses = classNames({
    'm-0': !isMobile,
    'action-toolbar-actions': !isMobile,
    'photo-group-toolbar-mobile': isMobile,
    visible: isMobile && openNav,
  });

  const deleteBtn = !!selectedPhotos?.length && (
    <Button
      btnStyle="link"
      className={deleteButtonClasses}
      onClick={onPhotosDelete}
      disabled={disabled}
    >
      {!isMobile && <TrashIcon size={18} color="#fe5851" className="mr-2" />}
      Delete {!isMobile && photoLabel}
    </Button>
  );

  const actionBtns = !!selectedPhotos?.length && (
    <div className={buttonContainerClasses}>
      {onPhotosDownload && (
        <Fragment>
          {canAccessOriginalDownloadsOnBasename(basename) || isContributor ? (
            <span className="action-toolbar-action photo-download dropdown-menu-dark">
              <DropdownMenu
                placement="top-start"
                toggle={
                  <Button
                    btnStyle="link"
                    className={buttonClasses}
                    disabled={disabled}
                  >
                    Download
                  </Button>
                }
                closeOnClickWithin
              >
                <Button
                  btnStyle="reset"
                  className="dropdown-item h-auto-i"
                  disabled={disabled}
                  onClick={() =>
                    onPhotosDownload({ downloadOriginalPhoto: false })
                  }
                >
                  <p className="mb-0">
                    Download hi-res
                    <br />
                    <span className="text-light text-xs">
                      Great for prints up to 8x10
                    </span>
                  </p>
                </Button>
                <Button
                  btnStyle="reset"
                  className="dropdown-item h-auto-i"
                  disabled={disabled}
                  onClick={() =>
                    onPhotosDownload({ downloadOriginalPhoto: true })
                  }
                >
                  <p className="mb-0">
                    Download original
                    <br />
                    <span className="text-light text-xs">
                      Great for large format printing
                    </span>
                  </p>
                </Button>
              </DropdownMenu>
            </span>
          ) : (
            <span className="action-toolbar-action photo-download">
              <Button
                btnStyle="link"
                className={buttonClasses}
                onClick={onPhotosDownload}
                disabled={disabled}
              >
                Download
              </Button>
            </span>
          )}
        </Fragment>
      )}
      <span className="action-toolbar-action add-tags dropdown-menu-dark">
        <DropdownMenu
          placement="top-start"
          toggle={
            <Button
              btnStyle="link"
              className={buttonClasses}
              disabled={disabled}
            >
              Add Tags
            </Button>
          }
          closeOnClickWithin
        >
          <Button
            btnStyle="reset"
            className="dropdown-item"
            onClick={() => {
              openModal(PHOTO_ALBUM_FOLDER_TAGS, {
                modalTitle: `Manage tags for ${pluralize(
                  'this',
                  totalSelectedPhotos,
                )} ${pluralize(
                  'photo',
                  totalSelectedPhotos,
                )} (${totalSelectedPhotos})`,
              });
            }}
            disabled={disabled}
          >
            Add custom tags
          </Button>
          <Button
            btnStyle="reset"
            className="dropdown-item"
            onClick={() => {
              onFaceTagModeToggle();
            }}
            disabled={disabled}
          >
            Add face tags
          </Button>
          {onAddFauxFaceTags && availableIdentities?.length > 0 && (
            <Button
              btnStyle="reset"
              className="dropdown-item"
              onClick={() => {
                onAddFauxFaceTags();
              }}
              disabled={disabled}
            >
              Add faux tags
            </Button>
          )}
        </DropdownMenu>
      </span>
      {onPhotosHide && !isReferencePhotosAlbum && (
        <span className="action-toolbar-action hide-photos">
          <Button
            btnStyle="link"
            className={buttonClasses}
            onClick={onPhotosHide}
            disabled={disabled}
          >
            Hide {photoLabel}
          </Button>
        </span>
      )}
      <span className="action-toolbar-action">
        <Button
          btnStyle="link"
          className={buttonClasses}
          onClick={() =>
            openModal(PRO_MULTI_TARGET_SELECTION_MODAL, {
              modalTitle: 'Select where to move your photos...',
            })
          }
          disabled={disabled}
        >
          Move {photoLabel}&hellip;
        </Button>
      </span>
      {onRemoveFaceTags && (
        <span className="action-toolbar-action">
          <Button
            btnStyle="link"
            className={buttonClasses}
            onClick={onRemoveFaceTags}
          >
            Remove face tags
          </Button>
        </span>
      )}
      {isMobile && <span className="action-toolbar-action">{deleteBtn}</span>}
    </div>
  );

  return (
    <div className={classes}>
      <div className="d-flex flex-column align-items-start">
        <h4 className="mb-0">
          {totalSelectedPhotos} {photoLabel.toLowerCase()} selected
        </h4>
        <div className="mt-1">
          <Button
            btnStyle="link"
            className="text-sm"
            onClick={dismissPhotoGroupToolbar}
          >
            {selectedPhotos?.length ? 'Deselect All' : 'Close'}
          </Button>
        </div>
      </div>
      {isMobile ? (
        <div className="d-relative">
          <Button
            btnStyle="primary"
            btnSize="sm"
            onClick={() => setOpenNav(!openNav)}
            onBlur={() => setTimeout(() => setOpenNav(false), 100)}
            disabled={(selectedPhotos?.length ?? 0) === 0}
            className=""
          >
            Pick an action{' '}
            {openNav ? (
              <Angle direction="down" className="ml-1" />
            ) : (
              <Angle direction="up" className="ml-1" />
            )}
          </Button>
          {actionBtns}
        </div>
      ) : (
        actionBtns
      )}
      {!isMobile && deleteBtn}
    </div>
  );
};

AlbumManualMatchingToolbar.propTypes = propTypes;

export default AlbumManualMatchingToolbar;
